/**
 * AI cost tracking client (ADR-0030 Slice 2.5) — the "Costi AI" page reads the
 * LLM-spend aggregates from `admin/routes/ai_usage.py` (gated `odino:use`).
 */
import { api } from "./api";
import { useAuthStore } from "./auth";
import { consumeSse } from "./sseStream";

export interface UsageBucket {
  /** ISO day (by_day) or surface/model name; null on the grand totals. */
  key: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  cost_usd: number;
  turns: number;
}

export interface UsageReport {
  days: number;
  totals: UsageBucket;
  by_day: UsageBucket[];
  by_surface: UsageBucket[];
  by_model: UsageBucket[];
}

export const aiApi = {
  usage: (days = 30) => api.get<UsageReport>(`/ai/usage?days=${days}`),
};

// --- Executions (LLM run-log) — the per-turn detail behind the cost aggregates -

/** One LLM call as recorded in the `ai_usage` ledger — a single agent turn on
 *  some surface (ask / odino / voice / chat / team:<key>). The "Esecuzioni" page
 *  is the per-turn drill-down; "Costi AI" is the aggregate of these same rows. */
export interface ExecutionRow {
  id: string;
  created_at: string | null;
  surface: string;
  provider: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  cost_usd: number;
  /** The actor's raw id (operator user id or app_user id); never linked — the
   *  realm is inferred from the surface, not resolved. Null for system turns. */
  created_by: string | null;
  /** The bound LLM config; null ⇒ the default config was used ("Predefinita"). */
  config_id: string | null;
}

export interface ExecutionsResponse {
  total: number;
  page: number;
  limit: number;
  rows: ExecutionRow[];
  /** Distinct surface values present — drives the filter dropdown (never
   *  hardcode the surface vocabulary; the backend is the source of truth). */
  surfaces: string[];
}

export interface ExecutionsQuery {
  page?: number;
  limit?: number;
  surface?: string | null;
  model?: string | null;
  provider?: string | null;
  days?: number;
}

/** Build a query string from the defined params, skipping null/undefined/"". */
function buildQuery(params: ExecutionsQuery): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    sp.set(k, String(v));
  }
  return sp.toString();
}

export const executionsApi = {
  list: (params: ExecutionsQuery) =>
    api.get<ExecutionsResponse>(`/ai/executions?${buildQuery(params)}`),
};

// --- LLM configs (ADR-0033 Phase A) — the multi-config CRUD ---------------

export interface LlmProviderInfo {
  provider_id: string;
  label: string;
  description: string;
  capabilities: string[];
  default_models: string[];
  requires_api_key: boolean;
  requires_base_url: boolean;
  base_url_hint: string;
  /** ADR-0035 RAG — whether this provider can produce embeddings (KB binding). */
  supports_embeddings: boolean;
  /** Model-field hints when the config kind is `embedding`. */
  default_embedding_models: string[];
}

export interface LlmConfig {
  id: string;
  name: string;
  provider: string;
  model: string;
  /** ADR-0035 RAG — `chat` (assistant model) or `embedding` (a Knowledge Base binds it). */
  kind: string;
  base_url: string | null;
  configured: boolean;
  has_credential: boolean;
  /** ADR-0034: the EFFECTIVE native-input capabilities (declared on the row, else the
   *  per-(provider,model) suggestion). A subset of `["image","pdf"]`; `[]` = text-only. */
  input_modalities: string[];
  /** Whether `input_modalities` was DECLARED (vs derived from the suggestion). */
  modalities_declared: boolean;
  rate_limit_rpm: number | null;
  enabled: boolean;
  is_default: boolean;
  spend_this_month: number;
  created_at: string | null;
}

/** The native-input modalities a chat surface's resolved model accepts (ADR-0034 badge). */
export interface AssistantCapabilities {
  model: string | null;
  provider: string | null;
  /** Subset of `["image","pdf"]`; empty ⇒ text-only (images/PDF go to the file tools). */
  vision: string[];
  configured: boolean;
}

export interface LlmConfigsResponse {
  configs: LlmConfig[];
  providers: LlmProviderInfo[];
}

export interface LlmConfigInput {
  name: string;
  provider: string;
  model: string;
  /** ADR-0035 — `chat` (default) or `embedding`. */
  kind?: string;
  /** Supply on create / to rotate; omit to keep. Optional for keyless providers. */
  api_key?: string;
  base_url?: string;
  /** ADR-0034: declared native-input caps (subset of `["image","pdf"]`). `null`/omit ⇒
   *  un-declared (runtime falls back to the suggestion); `[]` ⇒ text-only. */
  input_modalities?: string[] | null;
  rate_limit_rpm?: number | null;
  enabled?: boolean;
  is_default?: boolean;
}

export const llmConfigsApi = {
  list: () => api.get<LlmConfigsResponse>("/ai/llm-configs"),
  create: (body: LlmConfigInput) => api.post<LlmConfig>("/ai/llm-configs", body),
  update: (id: string, body: LlmConfigInput) => api.put<LlmConfig>(`/ai/llm-configs/${id}`, body),
  setDefault: (id: string) => api.post<LlmConfig>(`/ai/llm-configs/${id}/default`, {}),
  remove: (id: string) => api.delete<void>(`/ai/llm-configs/${id}`),
  /** The suggested native-input caps for a (provider, model) — pre-fills the dialog (ADR-0034). */
  modalitySuggestion: (provider: string, model: string) =>
    api.get<{ caps: string[] }>(
      `/ai/llm-configs/modality-suggestion?provider=${encodeURIComponent(provider)}&model=${encodeURIComponent(model)}`,
    ),
};

// --- Agents (ADR-0033 Phase B) — the per-surface config binding -------------

export interface AiAgent {
  id: string;
  key: string;
  name: string;
  /** `ask`/`voice`/`chat`/`odino` for system rows; `custom` otherwise. */
  strategy: string;
  enabled: boolean;
  is_system: boolean;
  description: string | null;
  system_prompt: string | null;
  /** The custom agent's capability grant (empty for system agents). */
  capabilities: string[];
  /** Attached custom tools (ADR-0033 Phase D) — CustomTool ids (empty for system). */
  tool_ids: string[];
  /** Attached MCP servers (ADR-0033 Phase E) — McpServer ids (empty for system). */
  mcp_ids: string[];
  /** The EXPLICIT binding (null ⇒ inherit the default). */
  llm_config_id: string | null;
  /** The RESOLVED config the surface actually uses (binding → default). */
  effective_config_id: string | null;
  effective_config_name: string | null;
  created_at: string | null;
}

export interface AiAgentsResponse {
  agents: AiAgent[];
  /** The safe capability vocabulary a custom agent may be granted. */
  safe_capabilities: string[];
  /** The read-only default a new custom agent starts with. */
  default_capabilities: string[];
}

export interface AiAgentInput {
  llm_config_id: string | null;
  /** Custom-agent only now — a system agent is ALWAYS on (gate access via RBAC),
   * so the system-agent save omits it; sending it for a system agent → 422. */
  enabled?: boolean;
  /** Custom-agent only — rejected (422) for a system agent. */
  name?: string;
  description?: string | null;
  system_prompt?: string | null;
  capabilities?: string[];
  tool_ids?: string[];
  mcp_ids?: string[];
}

export interface AiCustomAgentCreate {
  key: string;
  name: string;
  description?: string | null;
  system_prompt?: string | null;
  capabilities?: string[];
  tool_ids?: string[];
  mcp_ids?: string[];
  llm_config_id?: string | null;
  enabled?: boolean;
}

export const agentsApi = {
  list: () => api.get<AiAgentsResponse>("/ai/agents"),
  create: (body: AiCustomAgentCreate) => api.post<AiAgent>("/ai/agents", body),
  update: (id: string, body: AiAgentInput) => api.put<AiAgent>(`/ai/agents/${id}`, body),
  remove: (id: string) => api.delete<void>(`/ai/agents/${id}`),
};

// --- Tools (ADR-0033 Phase D) — sandbox-backed atomic functions --------------

export interface AiTool {
  id: string;
  key: string;
  name: string;
  description: string | null;
  language: string;
  code: string;
  /** TypeScript SOURCE for a `javascript` tool (the editor re-loads it); null for Python.
   *  `code` holds the transpiled JS. */
  code_ts: string | null;
  input_schema: Record<string, unknown>;
  libraries: string[];
  output_files: string[];
  timeout_seconds: number;
  enabled: boolean;
  /** Derived — which args are FILE inputs (x-tray-file); the test-run uploads them. */
  file_params: string[];
  created_at: string | null;
}

export interface AiToolsResponse {
  tools: AiTool[];
  /** The sandbox languages a tool may use. */
  languages: string[];
}

export interface AiToolLanguage {
  name: string;
  version: string;
  /** The pip libraries the sandbox ALLOWS for this language (a static image pin). */
  libraries: string[];
  use_cases: string[];
}

export interface AiToolLanguagesResponse {
  /** False when the sandbox sidecar is unset / unreachable (the console shows a hint). */
  sandbox_configured: boolean;
  languages: AiToolLanguage[];
}

export interface AiToolCreate {
  key: string;
  name: string;
  description?: string | null;
  language?: string;
  code: string;
  /** TS source for a `javascript` tool (`code` is its FE-transpiled JS). */
  code_ts?: string | null;
  input_schema?: Record<string, unknown> | null;
  libraries?: string[];
  output_files?: string[];
  timeout_seconds?: number;
  enabled?: boolean;
}

export interface AiToolUpdate {
  name?: string;
  description?: string | null;
  language?: string;
  code?: string;
  code_ts?: string | null;
  input_schema?: Record<string, unknown> | null;
  libraries?: string[];
  output_files?: string[];
  timeout_seconds?: number;
  enabled?: boolean;
}

export interface AiToolOutputFile {
  name: string;
  mime: string;
  size: number;
  ref: string;
}

export interface AiToolRunResult {
  ok: boolean;
  exit_code: number;
  output: unknown;
  stdout: string;
  stderr: string;
  files: AiToolOutputFile[];
  error: string | null;
}

export const toolsApi = {
  list: () => api.get<AiToolsResponse>("/ai/tools"),
  get: (id: string) => api.get<AiTool>(`/ai/tools/${id}`),
  /** The sandbox's language + library manifest — the editor shows the available
   *  libraries + validates a tool's declared libraries against the same allow-list. */
  languages: () => api.get<AiToolLanguagesResponse>("/ai/tools/languages"),
  create: (body: AiToolCreate) => api.post<AiTool>("/ai/tools", body),
  update: (id: string, body: AiToolUpdate) => api.put<AiTool>(`/ai/tools/${id}`, body),
  remove: (id: string) => api.delete<void>(`/ai/tools/${id}`),
  /** Test-run a tool: `args` is the call args; `files` are the file inputs (FormData). */
  run: (id: string, args: Record<string, unknown>, files: File[]) => {
    const fd = new FormData();
    fd.append("args", JSON.stringify(args));
    for (const f of files) fd.append("files", f, f.name);
    return api.upload<AiToolRunResult>(`/ai/tools/${id}/run`, fd);
  },
  /** Fetch a harvested output file (authenticated) + trigger a client-side
   *  download with its filename (mirrors apps' downloadAttachment). */
  downloadOutput: async (ref: string, filename: string) => {
    const { useAuthStore } = await import("./auth");
    const { triggerBlobDownload } = await import("./api-workflows");
    const token = useAuthStore.getState().token;
    const res = await fetch(`/api/admin/ai/tools/outputs/${ref}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed: ${res.status} ${res.statusText}`);
    triggerBlobDownload(await res.blob(), filename || "output");
  },
};

// --- MCP servers (ADR-0033 Phase E) — network tool providers -----------------

/** Non-secret OAuth state for the status panel (the secrets live in the vault). */
export interface AiMcpOAuthStatus {
  method: string;
  connected: boolean;
  client_id: string | null;
  redirect_uri: string | null;
  scopes: string | null;
  expires_at: string | null;
  has_registration: boolean;
}

export interface AiMcpServer {
  id: string;
  key: string;
  name: string;
  description: string | null;
  transport: string;
  url: string | null;
  /** A `http_bearer` / `api_key` credential whose secret becomes the auth header. */
  headers_credential_id: string | null;
  /** `none` (public) | `credential` (vault credential) | `oauth` (interactive OAuth 2.1). */
  auth_method: string;
  /** Non-secret OAuth state when `auth_method === "oauth"`, else null. */
  oauth: AiMcpOAuthStatus | null;
  tool_prefix: string | null;
  timeout_seconds: number;
  enabled: boolean;
  created_at: string | null;
}

export interface AiMcpServersResponse {
  servers: AiMcpServer[];
  /** Transports a server may use (Phase E ships `http` only). */
  transports: string[];
  /** Credential types whose secret becomes the auth header. */
  credential_types: string[];
}

export interface AiMcpServerCreate {
  key: string;
  name: string;
  description?: string | null;
  transport?: string;
  url: string;
  headers_credential_id?: string | null;
  auth_method?: string;
  tool_prefix?: string | null;
  timeout_seconds?: number;
  enabled?: boolean;
}

export interface AiMcpServerUpdate {
  name?: string;
  description?: string | null;
  transport?: string;
  url?: string;
  headers_credential_id?: string | null;
  auth_method?: string;
  tool_prefix?: string | null;
  timeout_seconds?: number;
  enabled?: boolean;
}

/** Optional manual client for an AS without Dynamic Client Registration (leave empty
 *  to let the backend register one). */
export interface AiMcpOAuthBeginBody {
  client_id?: string | null;
  client_secret?: string | null;
  scopes?: string | null;
}

export interface AiMcpOAuthBeginResult {
  authorize_url: string;
  redirect_uri: string;
}

export interface AiMcpTestBody {
  url: string;
  transport?: string;
  headers_credential_id?: string | null;
  tool_prefix?: string | null;
  timeout_seconds?: number;
}

export interface AiMcpTestResult {
  ok: boolean;
  tools_count: number;
  tool_names: string[];
  error: string | null;
}

export const mcpApi = {
  list: () => api.get<AiMcpServersResponse>("/ai/mcp-servers"),
  create: (body: AiMcpServerCreate) => api.post<AiMcpServer>("/ai/mcp-servers", body),
  update: (id: string, body: AiMcpServerUpdate) =>
    api.put<AiMcpServer>(`/ai/mcp-servers/${id}`, body),
  remove: (id: string) => api.delete<void>(`/ai/mcp-servers/${id}`),
  /** Test a connection from the dialog's current form values (works pre-save). */
  test: (body: AiMcpTestBody) => api.post<AiMcpTestResult>("/ai/mcp-servers/test", body),
  /** Start the interactive OAuth flow → returns the authorize URL to open in a popup. */
  oauthBegin: (id: string, body: AiMcpOAuthBeginBody) =>
    api.post<AiMcpOAuthBeginResult>(`/ai/mcp-servers/${id}/oauth/begin`, body),
  /** The server's current (non-secret) OAuth status — polled after the popup closes. */
  oauthStatus: (id: string) => api.get<AiMcpOAuthStatus>(`/ai/mcp-servers/${id}/oauth/status`),
  /** Revoke locally: drop the stored tokens + mark not-connected. */
  oauthDisconnect: (id: string) => api.post<AiMcpServer>(`/ai/mcp-servers/${id}/oauth/disconnect`, {}),
};

// --- Teams / Squadre (ADR-0033 Phase F) — router-based multi-agent delegation --

export interface AiTeam {
  id: string;
  key: string;
  name: string;
  description: string | null;
  /** The router agent (decides + composes); null ⇒ no valid router (run errors). */
  router_agent_id: string | null;
  /** The delegable member agents — Agent ids. */
  member_agent_ids: string[];
  /** Operator-authored routing policy woven into the router's prompt. */
  routing_instructions: string | null;
  enabled: boolean;
  created_at: string | null;
}

export interface AiTeamsResponse {
  teams: AiTeam[];
}

export interface AiTeamCreate {
  key: string;
  name: string;
  description?: string | null;
  router_agent_id: string;
  member_agent_ids?: string[];
  routing_instructions?: string | null;
  enabled?: boolean;
}

export interface AiTeamUpdate {
  name?: string;
  description?: string | null;
  router_agent_id?: string;
  member_agent_ids?: string[];
  routing_instructions?: string | null;
  enabled?: boolean;
}

export const teamsApi = {
  list: () => api.get<AiTeamsResponse>("/ai/teams"),
  create: (body: AiTeamCreate) => api.post<AiTeam>("/ai/teams", body),
  update: (id: string, body: AiTeamUpdate) => api.put<AiTeam>(`/ai/teams/${id}`, body),
  remove: (id: string) => api.delete<void>(`/ai/teams/${id}`),
  // The console run path is NOT exposed here — `streamTeamRun` (below) hardcodes
  // `/api/admin/ai/teams/${id}/run` directly (SSE-over-POST, the Ask wire shape).
};

/** One SSE event from a team console run — the same wire shape Ask emits. */
export interface TeamRunEvent {
  type: string;
  text?: string;
  message?: string;
  [k: string]: unknown;
}

/**
 * POST a question to a team's console runner and invoke `onEvent` per SSE frame
 * until the stream closes (mirrors `streamAskTurn`). The router delegates to its
 * members on the operator Ask boundary; an HTTP error surfaces as an `error` event.
 */
export async function streamTeamRun(
  teamId: string,
  text: string,
  onEvent: (ev: TeamRunEvent) => void,
  opts: { signal?: AbortSignal } = {},
): Promise<void> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`/api/admin/ai/teams/${teamId}/run`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ text, history: [] }),
    signal: opts.signal,
  });
  if (!res.ok || !res.body) {
    let message = `http_${res.status}`;
    try {
      const j = (await res.json()) as { detail?: string; message?: string };
      message = j.detail || j.message || message;
    } catch {
      /* keep the http_<status> fallback */
    }
    onEvent({ type: "error", message });
    return;
  }
  await consumeSse<TeamRunEvent>(res.body, onEvent);
}

// --- Sandbox monitoring (thin proxy over the sandbox sidecar's own ledger) ----
//
// The `code` sandbox sidecar persists every code run + every persistent workspace
// in its OWN DB; these read APIs are relayed by `admin/routes/ai_sandbox.py`
// (gated `odino:use`). Distinct from the LLM "Esecuzioni"/"Sessioni" surfaces:
// THESE are the sandbox's Python runs (compute / custom tools) + their workspaces.
// All list responses carry `sandbox_configured` — false when the sidecar is unset
// (`HUB_SANDBOX_SERVICE_URL`), so the page renders a "non configurata" empty state.

/** One sandbox code-run, list shape (newest first). */
export interface SandboxExecutionRow {
  execution_id: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  language: string;
  exit_code: number | null;
  status: string;
  has_result: boolean;
  /** Output filenames written by the run. */
  output_files: string[];
  /** First lines of the executed code (for the list preview). */
  code_preview: string;
  client_token_id: string | null;
  /** api | workflow | user_tool | agent — which surface fired the run. */
  origin: string | null;
  origin_ref: string | null;
}

/** Full run detail — code / streams / result / output files. NOTE: unlike the list
 *  row (where `output_files` is a `string[]` of filenames), the sidecar's DETAIL
 *  returns `output_files` as a list of OBJECTS `{name, mime, size_bytes}`. */
export interface SandboxExecutionDetail extends Omit<SandboxExecutionRow, "output_files"> {
  request_id: string | null;
  code: string;
  libraries: string[];
  attachment_names: string[];
  expected_outputs: string[];
  stdout: string;
  stderr: string;
  stdout_truncated: boolean;
  stderr_truncated: boolean;
  result: unknown;
  output_files: { name: string; mime: string; size_bytes: number }[];
  resource_limits: unknown;
  runtime: unknown;
  session_id: string | null;
}

export interface SandboxExecutionsResponse {
  executions: SandboxExecutionRow[];
  next_cursor: string | null;
  sandbox_configured: boolean;
}

/** One persistent workspace session, list shape. */
export interface SandboxSessionRow {
  session_id: string;
  created_at: string;
  last_accessed_at: string;
  expires_at: string;
  workspace_size_bytes: number;
  file_count: number;
  execution_count: number;
  client_token_id: string | null;
  metadata: Record<string, unknown>;
  status: string;
}

/** Session detail — metadata + the workspace file listing. */
export interface SandboxSessionDetail extends SandboxSessionRow {
  ttl_seconds: number;
  quota_warning?: boolean;
  workspace_files: {
    name: string;
    size_bytes: number;
    mime: string;
    modified_at: string;
  }[];
  workspace_files_truncated?: boolean;
}

export interface SandboxExecutionsQuery {
  limit?: number;
  cursor?: string | null;
  language?: string | null;
  status?: string | null;
  origin?: string | null;
  q?: string | null;
  session_id?: string | null;
}

export interface SandboxSessionsQuery {
  limit?: number;
  cursor?: string | null;
  status?: string | null;
  client_token_id?: string | null;
}

function sandboxQuery(params: SandboxExecutionsQuery | SandboxSessionsQuery): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    sp.set(k, String(v));
  }
  return sp.toString();
}

/** Stream a sandbox file THROUGH Tray (the sidecar token stays server-side) and
 *  trigger a client-side download (mirrors `toolsApi.downloadOutput`). */
async function downloadSandboxFile(url: string, filename: string): Promise<void> {
  const { triggerBlobDownload } = await import("./api-workflows");
  const token = useAuthStore.getState().token;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status} ${res.statusText}`);
  triggerBlobDownload(await res.blob(), filename || "file");
}

export const sandboxApi = {
  listExecutions: (params: SandboxExecutionsQuery) =>
    api.get<SandboxExecutionsResponse>(`/ai/sandbox/executions?${sandboxQuery(params)}`),
  getExecution: (id: string) =>
    api.get<SandboxExecutionDetail>(`/ai/sandbox/executions/${encodeURIComponent(id)}`),
  downloadExecutionFile: (id: string, filename: string) =>
    downloadSandboxFile(
      `/api/admin/ai/sandbox/executions/${encodeURIComponent(id)}/files/${encodeURIComponent(filename)}`,
      filename,
    ),
  listSessions: (params: SandboxSessionsQuery) =>
    api.get<{ sessions: SandboxSessionRow[]; next_cursor: string | null; sandbox_configured: boolean }>(
      `/ai/sandbox/sessions?${sandboxQuery(params)}`,
    ),
  getSession: (id: string) =>
    api.get<SandboxSessionDetail>(`/ai/sandbox/sessions/${encodeURIComponent(id)}`),
  downloadSessionFile: (id: string, filePath: string) =>
    downloadSandboxFile(
      // file_path may contain slashes (e.g. uploads/x.pdf); the path is a single
      // route segment server-side ({file_path:path}) so DON'T encode the slashes.
      `/api/admin/ai/sandbox/sessions/${encodeURIComponent(id)}/files/${filePath
        .split("/")
        .map(encodeURIComponent)
        .join("/")}`,
      filePath.split("/").pop() || filePath,
    ),
};
